import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ConfirmationDialog } from "ui/confirmation-dialog";
import toast from "components/partials/toast/toast";
import {
  cleanupFormDataForSave,
  getNestedValue,
  mergeTabFormData,
  setNestedValue,
} from "containers/admin/clients/client/components/form-builder/form-builder";
import {
  getFormSchemaSidebarItems,
  getSectionNeedsGeneration,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import type { FormPageTab } from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import { LATEST_CAMPAIGN_TEMPLATE_SCHEMA_VERSION } from "containers/admin/clients/client/campaign-templates-page/campaign-template.constants";
import type { CampaignTemplateDetailsLocationState } from "containers/admin/clients/client/campaign-templates-page/campaign-templates-page.types";
import {
  deriveEmailCreativeRefs,
  deriveSmsCreativeRefs,
  reconcileSmsChannelRows,
  type SmsChannelRow,
} from "containers/admin/clients/client/campaign-templates-page/sms-channel-rows.utils";
import {
  CHANNELS_TAB_ID,
  useSmsChannelValueOptions,
} from "containers/admin/clients/client/campaign-templates-page/use-sms-channel-value-options";
import {
  createStrategyFormTabs,
  getStrategyData,
  isDynamicFormSchema,
} from "containers/admin/clients/client/programs-page/program-strategy-form.utils";
import { useCampaignObjectiveCascadeOverrides } from "containers/admin/clients/client/programs-page/use-campaign-objective-cascade-overrides";

import {
  useCampaignTemplate,
  useCampaignTemplateSchema,
  useUpdateCampaignTemplate,
} from "hooks/api/use-campaign-templates";
import { useSyncedDraftData } from "hooks/use-synced-draft-data";
import { useTabDirtyState } from "hooks/use-tab-dirty-state";

const CAMPAIGN_DURATION_TAB_ID = "campaign-duration";

// Deployment dates pre-fills from Campaign timing's dates while it is still empty, then detaches
// permanently once the user enters a value of its own — the existing value always wins over the
// derived one. Computed on read, like the SMS reconciliation below, rather than written into
// `draftData`, so it keeps defaulting from Campaign timing across renders for as long as it's unset.
const withDeploymentDatesPrefill = (
  data: Record<string, unknown>,
  tab: FormPageTab,
  tabValues: Record<string, unknown>,
): Record<string, unknown> => {
  const deploymentDatesField = getStrategyData(tab.schema.properties.email?.properties).deploymentDates;

  if (!deploymentDatesField) {
    return tabValues;
  }

  const campaignTiming = getStrategyData(
    getNestedValue(data, [CAMPAIGN_DURATION_TAB_ID, "campaignTiming"]),
  );
  const deploymentDates = getStrategyData(getStrategyData(tabValues.email).deploymentDates);

  return setNestedValue(tabValues, ["email", "deploymentDates"], {
    ...deploymentDates,
    startDate: deploymentDates.startDate || campaignTiming.startDate || "",
    endDate: deploymentDates.endDate || campaignTiming.endDate || "",
  });
};

// SMS rows are derived from the student/parent creatives, not authored directly, so they are
// reconciled here — in the value-projection path every reader (FormBuilder, the viewer, the
// "needs generation" checks) goes through — rather than by rewriting `draftData`/`nextData`
// themselves, which would otherwise disturb `useTabDirtyState`'s comparison.
const getReconciledTabValues = (
  data: Record<string, unknown>,
  tab: FormPageTab,
): Record<string, unknown> => {
  const tabValues = getStrategyData(getNestedValue(data, tab.dataPath ?? [tab.id]));

  if (tab.id !== CHANNELS_TAB_ID) {
    return tabValues;
  }

  const smsRefs = deriveSmsCreativeRefs(data);
  const emailRefs = deriveEmailCreativeRefs(data);
  const existingSmsItems = getStrategyData(tabValues.sms).items;
  const reconciledSmsItems = reconcileSmsChannelRows(
    Array.isArray(existingSmsItems) ? (existingSmsItems as SmsChannelRow[]) : [],
    smsRefs,
    emailRefs,
  );
  const tabValuesWithSms = setNestedValue(tabValues, ["sms", "items"], reconciledSmsItems);

  return withDeploymentDatesPrefill(data, tab, tabValuesWithSms);
};

const cleanStrategyDataForSave = (
  tabs: FormPageTab[],
  data: Record<string, unknown>,
): Record<string, unknown> =>
  tabs.reduce((result, tab) => {
    const dataPath = tab.dataPath ?? [tab.id];
    const tabValues = getReconciledTabValues(result, tab);
    const cleanedTabValues = cleanupFormDataForSave(tab.schema, tabValues);

    return setNestedValue(result, dataPath, cleanedTabValues);
  }, data);

export const useCampaignTemplateForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams<{ templateId: string }>();
  const [isEditing, setIsEditing] = useState(() => {
    const state = location.state as CampaignTemplateDetailsLocationState | null;

    return Boolean(state?.openInEditMode);
  });
  const [tabRevisions, setTabRevisions] = useState<Record<string, number>>({});

  const { data: template, isLoading: isTemplateLoading } = useCampaignTemplate(templateId);
  const storedSchemaVersion = template?.schemaVersion;
  const needsLatestSchemaForSave =
    storedSchemaVersion != null && storedSchemaVersion < LATEST_CAMPAIGN_TEMPLATE_SCHEMA_VERSION;

  const { data: schemaResponse, isLoading: isSchemaLoading } = useCampaignTemplateSchema({
    version: storedSchemaVersion,
  });
  const { data: latestSchemaResponse } = useCampaignTemplateSchema(
    { version: LATEST_CAMPAIGN_TEMPLATE_SCHEMA_VERSION },
    { enabled: needsLatestSchemaForSave },
  );
  const updateTemplateMutation = useUpdateCampaignTemplate();

  const schema = useMemo(
    () => (isDynamicFormSchema(schemaResponse) ? schemaResponse : undefined),
    [schemaResponse],
  );
  const latestSchema = useMemo(
    () => (isDynamicFormSchema(latestSchemaResponse) ? latestSchemaResponse : undefined),
    [latestSchemaResponse],
  );

  const savedData = useMemo(() => getStrategyData(template?.data), [template?.data]);

  const { draftData, setDraftData, draftDataRef } = useSyncedDraftData({
    savedData,
    isEditing,
    isDataLoading: isTemplateLoading,
    resetKey: templateId ?? "",
  });

  const cascadeOverrides = useCampaignObjectiveCascadeOverrides({
    schemaVersion: template?.schemaVersion,
    programType: template?.programType,
    draftData,
    setDraftData,
    isEditing,
  });
  const fieldOverrides = useSmsChannelValueOptions(draftData, cascadeOverrides);

  const tabs = useMemo(
    () => (schema ? createStrategyFormTabs(schema, fieldOverrides) : []),
    [schema, fieldOverrides],
  );
  const saveTabs = useMemo(() => {
    const schemaForSave =
      needsLatestSchemaForSave && latestSchema ? latestSchema : schema;

    return schemaForSave ? createStrategyFormTabs(schemaForSave) : [];
  }, [needsLatestSchemaForSave, latestSchema, schema]);
  const sidebarItems = useMemo(
    () => (schema ? getFormSchemaSidebarItems(schema) : undefined),
    [schema],
  );

  const { hasChanges } = useTabDirtyState({ tabs, draftData, savedData, isEditing });

  const [draftName, setDraftName] = useState(template?.name ?? "");

  useEffect(() => {
    setDraftName(template?.name ?? "");
  }, [templateId, template?.name]);

  useEffect(() => {
    if (!isEditing) {
      setDraftName(template?.name ?? "");
    }
  }, [template?.name, isEditing]);

  const isNameDirty = draftName.trim() !== (template?.name ?? "").trim();
  const canSave = hasChanges || isNameDirty;

  const isTemplateIncomplete = useMemo(
    () => tabs.some((tab) => getSectionNeedsGeneration(tab.schema, getReconciledTabValues(draftData, tab))),
    [tabs, draftData],
  );

  useEffect(() => {
    const state = location.state as CampaignTemplateDetailsLocationState | null;

    if (state?.openInEditMode) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const getTabValues = (tab: FormPageTab) => getReconciledTabValues(draftData, tab);

  const handleTabValuesChange = (tab: FormPageTab, values: Record<string, unknown>) => {
    const dataPath = tab.dataPath ?? [tab.id];
    setDraftData((prev) => setNestedValue(prev, dataPath, values));
  };

  const bumpTabRevisions = () => {
    setTabRevisions((prev) =>
      Object.fromEntries(tabs.map((tab) => [tab.id, (prev[tab.id] ?? 0) + 1])),
    );
  };

  const handleDiscard = () => {
    setDraftData(savedData);
    setDraftName(template?.name ?? "");
    bumpTabRevisions();
  };

  const [pendingSave, setPendingSave] = useState<{
    name: string;
    data: Record<string, unknown>;
  } | null>(null);

  const withReconciledChannelsForSave = (data: Record<string, unknown>) => {
    const channelsTab = tabs.find((tab) => tab.id === CHANNELS_TAB_ID);

    if (!channelsTab) {
      return data;
    }

    const dataPath = channelsTab.dataPath ?? [channelsTab.id];

    return setNestedValue(data, dataPath, getReconciledTabValues(data, channelsTab));
  };

  const persistTemplate = (name: string, data: Record<string, unknown>) => {
    if (!templateId) {
      return;
    }

    if (needsLatestSchemaForSave && !latestSchema) {
      toast.error({
        data: {
          title: "Could not save campaign template",
          message: "Template schema is still loading. Please try again.",
        },
      });
      return;
    }

    const dataForSave = cleanStrategyDataForSave(
      saveTabs,
      withReconciledChannelsForSave(data),
    );

    updateTemplateMutation.mutate(
      {
        id: templateId,
        payload: {
          name,
          data: dataForSave,
        },
      },
      {
        onSuccess: (updatedTemplate) => {
          setDraftData(getStrategyData(updatedTemplate.data));
          setDraftName(updatedTemplate.name ?? name);
          toast.success({ data: { title: "Campaign brief saved" } });
          bumpTabRevisions();
          setIsEditing(false);
        },
        onError: () => {
          toast.error({
            data: {
              title: "Could not save campaign brief",
              message: "Please try again.",
            },
          });
        },
      },
    );
  };

  const handleSubmit = (formData: Record<string, unknown>, tab: FormPageTab) => {
    if (!templateId) {
      return;
    }

    const trimmedDraftName = draftName.trim();

    if (!trimmedDraftName) {
      toast.error({ data: { title: "Campaign brief name is required" } });
      return;
    }

    const dataPath = tab.dataPath ?? [tab.id];
    const existingTabData = getStrategyData(getNestedValue(draftDataRef.current, dataPath));
    const mergedTabData = mergeTabFormData(existingTabData, formData);
    const tabValues = cleanupFormDataForSave(tab.schema, mergedTabData);
    const nextData = setNestedValue(draftDataRef.current, dataPath, tabValues);

    const isMissingGenerationField = tabs.some((candidateTab) =>
      getSectionNeedsGeneration(candidateTab.schema, getReconciledTabValues(nextData, candidateTab)),
    );

    if (isMissingGenerationField) {
      setPendingSave({ name: trimmedDraftName, data: nextData });
      return;
    }

    persistTemplate(trimmedDraftName, nextData);
  };

  const handleConfirmSaveWithMissingFields = () => {
    if (!pendingSave) {
      return;
    }

    persistTemplate(pendingSave.name, pendingSave.data);
    setPendingSave(null);
  };

  const missingGenerationFieldsDialog = (
    <ConfirmationDialog
      open={pendingSave !== null}
      onClose={() => setPendingSave(null)}
      onConfirm={handleConfirmSaveWithMissingFields}
      headline="Are you sure you want to save this brief?"
      message="Some required information missing but you can still save this brief and complete it later."
      confirmButtonText="Save changes"
      cancelButtonText="Cancel"
      intent="warning"
    />
  );

  return {
    template,
    templateId,
    tabs,
    sidebarItems,
    isTemplateLoading,
    isSchemaLoading,
    isEditing,
    setIsEditing,
    hasChanges: canSave,
    isSaveDisabled: !canSave || !draftName.trim(),
    isTemplateIncomplete,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    missingGenerationFieldsDialog,
    draftName,
    handleNameChange: setDraftName,
    isSaving: updateTemplateMutation.isPending,
  };
};
